import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

const FAQS = [
  {
    question: 'How does Sortio handle file organization for consultants juggling many clients?',
    answer:
      "Sortio reads each deliverable's content and metadata locally, then routes it to the right per-client folder and project subfolder. If a folder already exists for that client, the file lands there. If it does not, Sortio can create a new client folder using your naming convention. You stay in control: every move is previewed and reversible.",
  },
  {
    question: 'I run three businesses out of one Downloads folder. How do I keep them separate?',
    answer:
      "Use the Rule Builder. Write a plain-English rule like 'move any files mentioning the holding company into a folder called Holdings' and another like 'file anything labeled with a project code into that project's folder.' Sortio applies the rules deterministically on your machine, so multi-business attribution stops being a year-end fire drill.",
  },
  {
    question: 'Can Sortio file grant and RFP response deliverables by client and project?',
    answer:
      "Yes. Sortio reads grant narratives, questionnaires, entity extracts, and RFP drafts to extract the funder, agency, or sponsor name plus the program code. A narrative for one funder lands in its own client/project subfolder; an application for a different funder goes somewhere else. Naming conventions can be enforced so dates, project codes, and version tags stay consistent.",
  },
  {
    question: 'Does Sortio work on incoming Downloads automatically, or do I have to run it manually?',
    answer:
      "Pro plans include Watch Folders, which monitor your Downloads, Desktop, or any folder you choose and apply your AI sort and Rule Builder rules to new files as they arrive, so Sortio quietly cleans up Downloads while you focus on client work.",
  },
  {
    question: 'How does Sortio handle a mixed personal and client Downloads folder on the same machine?',
    answer:
      "Sortio respects the blend. You can configure rules that route client work to your Dropbox client folders while personal items (tax docs, school PDFs, receipts) go to personal folders. Sortio reads files on your machine, so client files and your kids' permission slips can share a Mac. If you want client content to stay on device, run Offline mode (Ollama) or BYOK; by default Sortio uses our cloud to plan the sort and sends filenames and paths, with file contents sent only if you enable a content-based feature.",
  },
  {
    question: 'Can Sortio route files into existing client folders instead of inventing new ones?',
    answer:
      "Yes. Turn on 'Restrict to a folder list' in the Options panel and point Sortio at your existing client folder structure on disk. Sortio will only use those folders, with a fallback you choose for anything that does not clearly fit. This is ideal if you have a long-standing client folder taxonomy you want preserved exactly.",
  },
  {
    question: 'Does Sortio integrate with QuickBooks, Xero, or Notion?',
    answer:
      "Sortio organizes the files that surround those tools rather than connecting to them directly. Exports, invoices, receipts, and reports that you download from QuickBooks or Xero get routed into the right client or entity folder. Notion exports can be filed by workspace or project. The work happens on your local filesystem, where the source of truth often lives anyway.",
  },
  {
    question: 'Can I use Sortio to track billable hours by client folder?',
    answer:
      "Sortio is not a time tracker, but the audit log records when each deliverable was filed under which client. Many consultants pair that with their billing tool to reconcile work-in-progress against billable hours. Knowing exactly when a draft narrative landed under Credit Union A is often the missing piece for accurate invoicing.",
  },
  {
    question: 'I have years of deliverables scattered across Dropbox, OneDrive, and an old external drive. Can Sortio recover them?',
    answer:
      "Sortio works on any folder that syncs or mounts locally, including Dropbox, OneDrive, iCloud Drive, Google Drive sync, and external drives. Point it at the messy archive, and it can read across PDFs, Word docs, and slide decks to rebuild per-client structure. For very large archives, the AI Rule Builder is faster than full AI sort and runs entirely on your machine.",
  },
  {
    question: 'Can I enforce a retention policy across all my client folders?',
    answer:
      "You can build rules that flag, archive, or move files older than a given date into a retention folder per client. Combined with the audit log, this gives solo consultants a lightweight retention workflow without buying a separate document management system. You still control the final move, so nothing is deleted without your approval.",
  },
  {
    question: 'Is Sortio safe for confidential client work and NDAs?',
    answer:
      "Sortio reads your files on your Mac or PC. When it opens a grant narrative or a draft contract to extract the client name and project code, that read happens locally. If you need file content to stay on device, run Offline mode with a local LLM (Ollama), where nothing leaves your machine, or use Bring Your Own Key, where requests go from your machine directly to your own provider account. By default Sortio uses our cloud (OpenAI) to plan the sort and sends filenames and folder paths; file contents are sent only if you enable a content-based feature. That flexibility is what helps you keep confidential drafts on your machine for compliance work, grant applications, and engagements under NDA. All traffic is encrypted in transit (TLS 1.2+) and at rest.",
  },
  {
    question: 'What is the difference between AI sort and the Rule Builder, and when should I use each?',
    answer:
      "AI sort uses the model to read each file and decide where it belongs, which is ideal for messy inboxes and one-off cleanups under a few thousand files. The Rule Builder lets you write plain-English rules that run deterministically and instantly on any number of files, which is the right tool for ongoing Downloads triage and large archives. Most consultants use both: AI sort for the initial cleanup, Rule Builder for everything that follows.",
  },
  {
    question: 'How much does Sortio cost for a solo consultant or founder?',
    answer:
      "Sortio Pro is $14.99 per month or $99 per year. That includes a monthly AI allowance sized for heavy use, unlimited rule-based automations, Watch Folders, and a commercial license. There is also a free tier with a one-time AI trial allowance and rule-based sorting (up to 5 rules) so you can try the workflow on a sample client folder before upgrading.",
  },
  {
    question: 'Can a freelancer use one Sortio license across a laptop and a desktop?',
    answer:
      "Yes. Your Sortio purchase covers multiple devices on the same operating system, so a freelancer with a MacBook and an iMac is covered by one Mac license. A Windows license covers multiple Windows machines. Mac and Windows are separate licenses.",
  },
];

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mb-4">
      <div
        className={`bg-apple-800/40 backdrop-blur-sm rounded-2xl border ${
          isOpen ? 'border-sortio-primary/40' : 'border-apple-700/50'
        } transition-colors`}
      >
        <button
          className="flex justify-between items-center w-full p-5 text-left focus:outline-none"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
        >
          <span className="text-base font-medium text-apple-50 pr-4">{question}</span>
          <div
            className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
              isOpen ? 'bg-sortio-primary/20' : 'bg-apple-600/40'
            }`}
          >
            {isOpen ? (
              <ChevronUp className="w-3.5 h-3.5 text-sortio-primary" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5 text-apple-300" />
            )}
          </div>
        </button>
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="px-5 pb-5">
            <p className="text-sm text-apple-200 leading-relaxed">{answer}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConsultantsFAQ = () => {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <section id="faq" className="my-20">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="flex flex-col items-center text-center mb-12">
        <div className="mb-4 rounded-full bg-apple-700/60 backdrop-blur-sm p-2 border border-apple-600/30">
          <HelpCircle className="w-6 h-6 text-sortio-primary" />
        </div>
        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-apple-50">
          Consultant &amp; Multi-Venture FAQ
        </h2>
        <p className="mt-4 text-apple-300 max-w-2xl font-light">
          Per-client routing, multi-business attribution, grant deliverables, Rule Builder, and
          privacy options, answered for solo consultants and multi-venture founders.
        </p>
      </div>

      <div className="max-w-3xl mx-auto">
        {FAQS.map((faq, i) => (
          <FAQItem key={i} question={faq.question} answer={faq.answer} />
        ))}
      </div>
    </section>
  );
};

export default ConsultantsFAQ;
